import React, { useState, useEffect, useCallback } from 'react';
import { GamePhase, GameMessage, Question, Player, LobbyData, RevealData, LeaderboardData, PodiumData, AchievementsData, AnswerMessage, ScoreDetail } from './types/game';
import { useWebSocket } from './hooks/useWebSocket';
import JoinScreen from './components/screens/JoinScreen';
import LobbyScreen from './components/screens/LobbyScreen';
import CountdownScreen from './components/screens/CountdownScreen';
import ReadingScreen from './components/screens/ReadingScreen';
import QuestionScreen from './components/screens/QuestionScreen';
import RevealScreen from './components/screens/RevealScreen';
import AchievementsScreen from './components/screens/AchievementsScreen';
import LeaderboardScreen from './components/screens/LeaderboardScreen';
import PodiumScreen from './components/screens/PodiumScreen';
import ErrorScreen from './components/screens/ErrorScreen';
import WebGLBackground from './components/WebGLBackground';
import ReconnectingOverlay from './components/ReconnectingOverlay';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ThemeId } from './themes/types';

const AppContent: React.FC = () => {
  const { setTheme } = useTheme();
  const [currentPhase, setCurrentPhase] = useState<GamePhase>('JOIN');
  const [gameId, setGameId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [score, setScore] = useState(0);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [lastAnswerIdx, setLastAnswerIdx] = useState<number | null>(null);
  const [lobbyData, setLobbyData] = useState<LobbyData | null>(null);
  const [countdownData, setCountdownData] = useState<{ message: string; secondsLeft: number } | null>(null);
  const [readingData, setReadingData] = useState<Question | null>(null);
  const [revealData, setRevealData] = useState<RevealData | null>(null);
  const [achievementsData, setAchievementsData] = useState<AchievementsData | null>(null);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [podiumData, setPodiumData] = useState<PodiumData | null>(null);
  const [currentScoreDetail, setCurrentScoreDetail] = useState<ScoreDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldConnect, setShouldConnect] = useState(false);

  // Track all phase transition timestamps for timing analysis
  const [phaseTimings, setPhaseTimings] = useState({
    receivedReading: '',
    receivedQuestion: '',
    receivedReveal: '',
    receivedIntermission: ''
  });

  const handleMessage = useCallback((message: GameMessage) => {
    console.log('Received message:', message);

    switch (message.type) {
      case 'lobby':
        setCurrentPhase('LOBBY');
        setLobbyData(message.data);
        // Find and store current player ID
        const currentPlayer = message.data.players.find((p: Player) => p.name === playerName);
        if (currentPlayer) {
          setPlayerId(currentPlayer.id);
        }
        // Apply theme from server if provided
        if (message.data.theme && (message.data.theme === 'classic' || message.data.theme === 'retro-8bit' || message.data.theme === 'halloween')) {
          setTheme(message.data.theme as ThemeId);
        }
        break;
      case 'reconnect_token':
        // Store player ID from reconnect token
        if (message.data.playerId) {
          setPlayerId(message.data.playerId);
        }
        break;
      case 'countdown':
        setCurrentPhase('COUNTDOWN');
        setCountdownData(message.data);
        break;
      case 'reading':
        setCurrentPhase('READING');
        setReadingData(message.data);
        // Track when client received reading phase
        const readingReceivedAt = new Date().toISOString();
        setPhaseTimings(prev => ({ ...prev, receivedReading: readingReceivedAt }));
        console.log('📖 READING phase received at:', readingReceivedAt,
          'Server sent:', message.data.serverSentAt || 'unknown',
          'Question ID:', message.data.id);
        break;
      case 'question':
        setCurrentPhase('QUESTION');
        setCurrentQuestion(message.data);
        setHasAnswered(false);
        setLastAnswerIdx(null);
        // Track when client received the question (client's local time)
        const receivedAt = new Date().toISOString();
        setPhaseTimings(prev => ({ ...prev, receivedQuestion: receivedAt }));
        console.log('❓ QUESTION phase received at:', receivedAt,
          'Server sent:', message.data.serverSentAt || 'unknown',
          'Question ID:', message.data.id);
        break;
      case 'reveal':
        setCurrentPhase('REVEAL');
        setRevealData(message.data);
        // Track when client received reveal phase
        const revealReceivedAt = new Date().toISOString();
        setPhaseTimings(prev => ({ ...prev, receivedReveal: revealReceivedAt }));
        console.log('📊 REVEAL phase received at:', revealReceivedAt,
          'Server sent:', message.data.serverSentAt || 'unknown',
          'Question ID:', message.data.questionId);
        break;
      case 'achievements':
        setCurrentPhase('ACHIEVEMENTS');
        setAchievementsData(message.data);
        console.log('🏆 ACHIEVEMENTS phase received:', message.data.achievements?.length || 0, 'achievements');
        break;
      case 'leaderboard':
        setCurrentPhase('INTERMISSION');
        setLeaderboardData(message.data);
        // Track when client received intermission/leaderboard phase
        const intermissionReceivedAt = new Date().toISOString();
        setPhaseTimings(prev => ({ ...prev, receivedIntermission: intermissionReceivedAt }));
        console.log('🏆 INTERMISSION phase received at:', intermissionReceivedAt,
          'Server sent:', message.data.serverSentAt || 'unknown');
        // Update player's score if found in leaderboard
        const myPlayer = message.data.players.find((p: Player) => p.name === playerName);
        if (myPlayer) {
          setScore(myPlayer.score);
        }
        break;
      case 'podium':
        setCurrentPhase('PODIUM');
        setPodiumData(message.data);
        break;
      case 'answer_ack':
        console.log('Answer acknowledged:', message.data);
        break;
      case 'game_statistics':
        // Game statistics are sent to host - players can safely ignore
        console.log('Game statistics received (for host):', message.data);
        break;
      case 'error':
        console.error('Server error:', message.data);
        break;
      default:
        console.warn('Unknown message type:', message.type);
    }
  }, [playerName, setTheme]);

  const handleError = useCallback((error: string) => {
    setCurrentPhase('ERROR');
    setErrorMessage(error);
  }, []);

  const { isReconnecting, connectionStatus, connect, disconnect, sendMessage } = useWebSocket({
    gameId,
    playerName,
    onMessage: handleMessage,
    onError: handleError,
  });

  useEffect(() => {
    if (shouldConnect) {
      connect();
    }
  }, [shouldConnect, connect]);

  // Extract score detail when reveal data arrives and we have a player ID
  useEffect(() => {
    if (revealData?.scoreDetails && playerId) {
      const detail = revealData.scoreDetails[playerId];
      if (detail) {
        console.log('📊 Setting score detail for player', playerId, ':', detail);
        setCurrentScoreDetail(detail);
      } else {
        console.warn('⚠️ No score detail found for player', playerId, 'Available IDs:', Object.keys(revealData.scoreDetails));
        setCurrentScoreDetail(null);
      }
    }
  }, [revealData, playerId]);

  // Disconnect when game reaches PODIUM phase to prevent reconnection attempts
  useEffect(() => {
    if (currentPhase === 'PODIUM') {
      const timer = setTimeout(() => {
        disconnect();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, disconnect]);

  useEffect(() => {
    // Check URL params for game ID
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(part => part.length > 0);
    let urlGameId = '';

    if (pathParts.length > 0) {
      const extractedGameId = pathParts[pathParts.length - 1];
      if (extractedGameId && extractedGameId.length > 0) {
        urlGameId = extractedGameId.toUpperCase();
        setGameId(urlGameId);
      }
    }

    // Try to restore previous session from localStorage only if no URL game ID
    const savedGameId = localStorage.getItem('quizcrunch_gameId');
    const savedPlayerName = localStorage.getItem('quizcrunch_playerName');

    if (!urlGameId && savedGameId) {
      setGameId(savedGameId);
    }

    if (savedPlayerName) {
      setPlayerName(savedPlayerName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoinGame = (gId: string, pName: string) => {
    const upperGameId = gId.toUpperCase();
    setGameId(upperGameId);
    setPlayerName(pName);
    localStorage.setItem('quizcrunch_gameId', upperGameId);
    localStorage.setItem('quizcrunch_playerName', pName);
    setShouldConnect(true);
  };

  const handleAnswerSelect = (choiceIdx: number) => {
    if (hasAnswered || !currentQuestion) return;

    setHasAnswered(true);
    setLastAnswerIdx(choiceIdx);

    const answerSentAt = new Date().toISOString();

    // Send answer with comprehensive client timing information for lag analysis
    const answerPayload: AnswerMessage = {
      type: 'answer',
      data: {
        questionId: currentQuestion.id,
        choiceIdx: choiceIdx,
        clientTimestamp: answerSentAt,  // When client is sending the answer
        clientReceivedReading: phaseTimings.receivedReading,   // When client received reading phase
        clientReceivedQuestion: phaseTimings.receivedQuestion, // When client received question phase
        clientReceivedReveal: phaseTimings.receivedReveal,     // When client received reveal phase (usually empty for answers)
      },
    };

    console.log('📤 Sending answer with comprehensive timing:', {
      questionId: currentQuestion.id,
      choiceIdx,
      clientReceivedReading: phaseTimings.receivedReading,
      clientReceivedQuestion: phaseTimings.receivedQuestion,
      clientTimestamp: answerSentAt,
      readingToQuestion: phaseTimings.receivedQuestion && phaseTimings.receivedReading ?
        new Date(phaseTimings.receivedQuestion).getTime() - new Date(phaseTimings.receivedReading).getTime() : 'N/A',
      questionToAnswer: new Date(answerSentAt).getTime() - new Date(phaseTimings.receivedQuestion).getTime()
    });

    sendMessage(answerPayload);
  };

  const handleResetGame = () => {
    disconnect();
    setCurrentPhase('JOIN');
    setScore(0);
    setHasAnswered(false);
    setCurrentQuestion(null);
    setGameId('');
    setPlayerName('');
    localStorage.removeItem('quizcrunch_gameId');
    localStorage.removeItem('quizcrunch_playerName');
    setShouldConnect(false);
  };

  const handleReconnect = () => {
    setCurrentPhase('JOIN');
    setShouldConnect(true);
  };

  return (
    <div className="min-h-screen relative">
      <WebGLBackground gameState={currentPhase} />

      {/* Subtle floating elements - only show during lobby and podium phases */}
      {(currentPhase === 'LOBBY' || currentPhase === 'PODIUM') && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-30">
          <div className="absolute animate-bounce text-2xl" style={{ top: '15%', right: '10%', animationDelay: '0s', animationDuration: '3s' }}>✨</div>
          <div className="absolute animate-bounce text-2xl" style={{ bottom: '25%', left: '15%', animationDelay: '1s', animationDuration: '4s' }}>🌟</div>
          <div className="absolute animate-bounce text-2xl" style={{ top: '50%', left: '85%', animationDelay: '2s', animationDuration: '3.5s' }}>🎊</div>
        </div>
      )}

      <div className="relative z-10">
        {currentPhase === 'JOIN' && (
          <JoinScreen
            gameId={gameId}
            playerName={playerName}
            connectionStatus={connectionStatus}
            onJoinGame={handleJoinGame}
          />
        )}

        {currentPhase === 'LOBBY' && lobbyData && (
          <LobbyScreen
            gamePin={lobbyData.gamePin}
            players={lobbyData.players}
          />
        )}

        {currentPhase === 'COUNTDOWN' && countdownData && (
          <CountdownScreen
            message={countdownData.message}
            secondsLeft={countdownData.secondsLeft}
          />
        )}

        {currentPhase === 'READING' && readingData && (
          <ReadingScreen
            question={readingData}
            secondsLeft={readingData.secondsLeft || 5}
          />
        )}

        {currentPhase === 'QUESTION' && currentQuestion && (
          <QuestionScreen
            question={currentQuestion}
            score={score}
            hasAnswered={hasAnswered}
            onAnswerSelect={handleAnswerSelect}
          />
        )}

        {currentPhase === 'REVEAL' && revealData && currentQuestion && (
          <RevealScreen
            correctAnswer={currentQuestion.choices[revealData.correctIdx]}
            evidence={revealData.evidence}
            secondsLeft={revealData.secondsLeft}
            wasCorrect={hasAnswered && lastAnswerIdx === revealData.correctIdx}
            scoreDetail={currentScoreDetail || undefined}
          />
        )}

        {currentPhase === 'ACHIEVEMENTS' && (
          <AchievementsScreen achievements={achievementsData?.achievements || []} />
        )}

        {currentPhase === 'INTERMISSION' && leaderboardData && (
          <LeaderboardScreen
            players={leaderboardData.players}
            currentPlayerName={playerName}
          />
        )}

        {currentPhase === 'PODIUM' && podiumData && (
          <PodiumScreen
            winners={podiumData.winners}
            onPlayAgain={handleResetGame}
          />
        )}

        {currentPhase === 'ERROR' && (
          <ErrorScreen
            errorMessage={errorMessage}
            onReconnect={handleReconnect}
          />
        )}

        {/* Show reconnecting overlay when connection is lost (but not on JOIN screen) */}
        {isReconnecting && currentPhase !== 'JOIN' && (
          <ReconnectingOverlay connectionStatus={connectionStatus} />
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider defaultTheme="classic">
      <AppContent />
    </ThemeProvider>
  );
};

export default App;